import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { C } from './constants/colors.js';
import { F, N } from './constants/fonts.js';
import { APP_THEME, GAME_MODE_THEMES } from './constants/themes.js';
import './styles/animations.css';

// Lib imports
import { SK, loadApp, saveApp, setSaveFailToast } from './lib/storage.js';
import { getSupabase, supaGetSession, supaGetUser, supaSignOut, syncToSupabase, pullFromSupabase } from './lib/supabase.js';
import { createProfile, createTracker, record, doneRound } from './lib/tracker.js';
import { initBkt } from './lib/bkt.js';
import { todayStr } from './lib/utils.js';
import { prefetchContent } from './lib/content-fetch.js';
import { buildSessionPlaylist } from './lib/playlist.js';
import { analyzeSession } from './lib/ai.js';
import { checkAndSendWelcomeEmail, checkAndSendEmailTriggers, saveParentEmail } from './lib/email.js';
import { checkAndArchiveSpelling } from './lib/spelling.js';
import { ccCount } from './lib/content-cache.js';
import { isGuess, isPatternTapping } from './lib/guardrails.js';
import { flushSyncQueue, setPilotSyncContext, syncAssessment, syncBKTSnapshot, syncSession } from './lib/pilotSync.js';
import { screenTransition } from './lib/motionPresets.js';
import { resetInsightSession } from './config/insightMessages.js';

// Hooks
import { useOnline } from './hooks/useOnline.js';
import { useTimer } from './hooks/useTimer.js';

// UI Components
import { TB } from './components/ui/TimerBar.jsx';
import { OceanBg } from './components/ui/OceanBg.jsx';

// Game Components
import { PhonicsGame } from './components/games/PhonicsGame.jsx';
import { SightGame } from './components/games/SightGame.jsx';
import { StoryGame } from './components/games/StoryGame.jsx';
import { BubblePop } from './components/games/BubblePop.jsx';

// Screen Components
import { Profiles } from './components/screens/Profiles.jsx';
import { Quiz } from './components/screens/Quiz.jsx';
import { PlacementBriefing } from './components/screens/PlacementBriefing.jsx';
import { PlacementAnalysis } from './components/screens/PlacementAnalysis.jsx';
import { PlacementResults } from './components/screens/PlacementResults.jsx';
import { ReadingDNA } from './components/screens/ReadingDNA.jsx';
import { ModePicker } from './components/screens/ModePicker.jsx';
import { SessionDone } from './components/screens/SessionDone.jsx';
import { LevelUpCeremony } from './components/screens/LevelUpCeremony.jsx';
import { Dash } from './components/screens/Dashboard.jsx';
import { StoryLibrary } from './components/screens/StoryLibrary.jsx';
import { StoryReading } from './components/screens/StoryReading.jsx';

// Playlist Components
import { PlaylistTransition, PlaylistStart, PlaylistChoice, PlaylistPlan, PlaylistProgress } from './components/playlist/PlaylistComponents.jsx';

// Auth Components
import { SignInScreen } from './components/auth/SignInScreen.jsx';
import { PasswordRecoveryScreen } from './components/auth/PasswordRecoveryScreen.jsx';

// Onboarding
import { Onboarding } from './components/onboarding/Onboarding.jsx';
import { ParentSetup } from './components/onboarding/ParentSetup.jsx';

// Parent Components
import { PinGate } from './components/parent/PinGate.jsx';
import { ParentDashboard } from './components/parent/ParentDashboard.jsx';

// Modal Components
import { PricingModal } from './components/PricingModal.jsx';
import { FeedbackModal } from './components/FeedbackModal.jsx';
import { FeedbackButton } from './components/FeedbackButton.jsx';

const OCEAN_SCREENS=['parent_setup','onboarding','add_child','profiles','placement_briefing','placement_analysis','placement_results','reading_dna','quiz','pin_gate','sign_in','password_recovery'];
const DEFAULT_SETTINGS = {
  tmr: 15,
  finleyChoose: true,
  coachingPromptsEnabled: true,
  dailyLimitMin: 45,
  difficultyOverride: 'auto',
  todayExtensionMin: 0,
  todayExtensionDate: null,
  notificationPrefs: {
    sessionReminders: true,
    weeklyProgressReport: true,
    parentTipsUpdates: true,
    milestoneAlerts: true
  }
};

function normalizeSettings(nextSettings = {}) {
  const merged = {
    ...DEFAULT_SETTINGS,
    ...nextSettings,
    notificationPrefs: {
      ...DEFAULT_SETTINGS.notificationPrefs,
      ...(nextSettings?.notificationPrefs || {})
    }
  };
  merged.coachingPromptsEnabled = merged.coachingPromptsEnabled !== false;
  // TODO(C20): Add a scheduled midnight rollover so today's extension clears without requiring a reload.
  if (merged.todayExtensionDate !== todayStr()) {
    merged.todayExtensionMin = 0;
    merged.todayExtensionDate = todayStr();
  }
  return merged;
}

function FinleyDesktopLogo({ size = 42 }) {
  return (
    <div style={{ position:'relative', width:size, height:size, flexShrink:0 }}>
      <svg viewBox="0 0 32 32" fill="none" style={{ width:'100%', height:'100%' }}>
        <rect width="32" height="32" rx="8" fill="#061928"/>
        <path d="M6 10h20M6 16h14M6 22h18" stroke="#00B4D8" strokeWidth="2.5" strokeLinecap="round"/>
        <circle cx="26" cy="22" r="4" fill="#FF6B35" opacity="0.9"/>
      </svg>
    </div>
  );
}

function DesktopContextPanel() {
  const bulletStyle = { display:'flex', alignItems:'flex-start', gap:12, padding:'12px 0', color:'rgba(255,255,255,0.82)' };
  const iconWrapStyle = { width:36, height:36, borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.08)' };

  return (
    <aside style={{ width:'100%', maxWidth:380, minWidth:300, borderRadius:32, padding:'36px 32px', display:'flex', flexDirection:'column', justifyContent:'space-between', background:'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)', border:'1px solid rgba(255,255,255,0.1)', boxShadow:'0 24px 80px rgba(0,0,0,0.25)', backdropFilter:'blur(10px)', position:'relative', zIndex:1 }}>
      <div>
        <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:24 }}>
          <FinleyDesktopLogo />
          <div>
            <div style={{ fontFamily:F, fontSize:28, color:'#fff', lineHeight:1 }}>Finley</div>
            <div style={{ fontFamily:N, fontSize:12, color:'rgba(255,255,255,0.45)', textTransform:'uppercase', letterSpacing:'0.14em', marginTop:4 }}>Adaptive reading app</div>
          </div>
        </div>

        <h2 style={{ fontFamily:F, fontSize:40, lineHeight:1.02, color:'#fff', margin:'0 0 16px', letterSpacing:'-0.03em' }}>The AI reading tutor that actually works</h2>
        <p style={{ fontFamily:N, fontSize:16, lineHeight:1.6, color:'rgba(255,255,255,0.62)', margin:'0 0 28px' }}>Finley meets your child where they are, then builds real reading momentum session by session.</p>

        <div style={{ display:'grid', gap:2 }}>
          <div style={bulletStyle}>
            <div style={iconWrapStyle}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00B4D8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9-6-18-3 9H2"/></svg>
            </div>
            <div style={{ fontFamily:N, fontSize:15, lineHeight:1.5 }}>Adapts to your child's exact level</div>
          </div>
          <div style={bulletStyle}>
            <div style={{ ...iconWrapStyle, background:'rgba(255,107,53,0.08)' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FF6B35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
            </div>
            <div style={{ fontFamily:N, fontSize:15, lineHeight:1.5 }}>Built on the Science of Reading</div>
          </div>
          <div style={bulletStyle}>
            <div style={{ ...iconWrapStyle, background:'rgba(255,209,102,0.08)' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FFD166" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            </div>
            <div style={{ fontFamily:N, fontSize:15, lineHeight:1.5 }}>COPPA compliant. No ads, ever.</div>
          </div>
        </div>
      </div>

      <div style={{ marginTop:28, paddingTop:24, borderTop:'1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ fontFamily:N, fontSize:13, color:'rgba(255,255,255,0.5)', marginBottom:14 }}>Best experienced on mobile or tablet</div>
        <div style={{ width:168, height:168, borderRadius:24, border:'1px dashed rgba(255,255,255,0.18)', background:'linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', padding:18 }}>
          <div style={{ width:70, height:70, borderRadius:16, background:'rgba(255,255,255,0.08)', display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:4, padding:10, marginBottom:12 }}>
            {Array.from({ length: 9 }).map((_, idx) => <span key={idx} style={{ borderRadius:4, background: idx % 2 === 0 ? 'rgba(255,255,255,0.8)' : 'rgba(0,180,216,0.45)' }}/>)}
          </div>
          <div style={{ fontFamily:N, fontSize:13, lineHeight:1.45, color:'rgba(255,255,255,0.72)' }}>Scan to open on your phone</div>
        </div>
      </div>
    </aside>
  );
}

export function App() {
  const online=useOnline();
  const[loading,setLoading]=useState(true);
  const[viewportWidth,setViewportWidth]=useState(()=>typeof window!=='undefined'?window.innerWidth:375);
  const[screen,setScreen]=useState("onboarding");const[profiles,setProfiles]=useState([]);const[apId,setApId]=useState(null);const[parentPin,setParentPin]=useState(null);const[settings,setSettings]=useState(normalizeSettings());const[dailyMap,setDailyMap]=useState({date:todayStr(),rounds:{},minutes:{}});const[sStars,setSStars]=useState(0);const[pinTarget,setPinTarget]=useState(null);const[expired,setExpired]=useState(false);const[pinSource,setPinSource]=useState(null);
  const[playlist,setPlaylist]=useState(null);const[playlistIdx,setPlaylistIdx]=useState(0);const[playlistSwaps,setPlaylistSwaps]=useState(1);
  const sessionStartRef=useRef(null);
  const[fbOpen,setFbOpen]=useState(false);const[fbScreen,setFbScreen]=useState(null);
  const[saveFail,setSaveFail]=useState(false);const[levelUpInfo,setLevelUpInfo]=useState(null);
  const[pricingOpen,setPricingOpen]=useState(false);const[checkoutMsg,setCheckoutMsg]=useState(null);
  const[storyToRead,setStoryToRead]=useState(null);
  const[parentDashboardTab,setParentDashboardTab]=useState('home');
  const[placementLevel,setPlacementLevel]=useState(null);
  const[placementNextAction,setPlacementNextAction]=useState('start');
  const[authUser,setAuthUser]=useState(null);
  const[recoveryMode,setRecoveryMode]=useState(false);
  const[sessionSummary,setSessionSummary]=useState({activitiesCompleted:0,wordsAttempted:0,wordsCorrect:0,score:0});
  const syncTimerRef=useRef(null);
  const roundStatsRef=useRef(null);
  const playlistIdRef=useRef(null);
  useEffect(()=>{setSaveFailToast(()=>{setSaveFail(true);setTimeout(()=>setSaveFail(false),5000);});return()=>setSaveFailToast(null);},[]);
  const ap=profiles.find(p=>p.id===apId);const tracker=ap?.tracker||createTracker(3);const ct=APP_THEME;
  const onboardingDataForDNA = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('rq:onboardingData') || '{}');
    } catch {
      return {};
    }
  }, [screen]);
  const minutesToday=dailyMap.minutes?.[apId]||0;
  const getSessionMinutes=()=>{if(!sessionStartRef.current)return 0;return Math.max(1,Math.round((Date.now()-sessionStartRef.current)/60000));};
  const addSessionMinutes=()=>{const mins=getSessionMinutes();if(mins>0)setDailyMap(dm=>{const today=todayStr();const base=dm.date!==today?{date:today,rounds:{},minutes:{}}:dm;return{...base,minutes:{...(base.minutes||{}),[apId]:(base.minutes?.[apId]||0)+mins}};});sessionStartRef.current=null;};
  const resetSessionSummary=()=>setSessionSummary({activitiesCompleted:0,wordsAttempted:0,wordsCorrect:0,score:0});
  const fbVisible=["mode","session_done","playlist_start","playlist_plan","playlist_choice","playlist_progress"].includes(screen);
  useEffect(()=>{window.openPricing=()=>setPricingOpen(true);return()=>{delete window.openPricing;};},[]);
  useEffect(()=>{
    const handleResize=()=>setViewportWidth(window.innerWidth);
    handleResize();
    window.addEventListener('resize',handleResize);
    return()=>window.removeEventListener('resize',handleResize);
  },[]);

  useEffect(()=>{
    const gameModes = ['phonics', 'sight', 'stories', 'bubbles'];
    if (gameModes.includes(screen) && ap) {
      roundStatsRef.current = {
        mode: screen,
        startedAt: Date.now(),
        attempted: 0,
        correct: 0,
        guessingTriggers: 0,
        patternTriggers: 0,
      };
      setPilotSyncContext({ childName: ap.name, sessionId: null });
    }
  }, [screen, apId]);

  useEffect(()=>{
    flushSyncQueue().catch(()=>{});
    const handleOnline = () => { flushSyncQueue().catch(()=>{}); };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  // ── MULTI-TAB SYNC: reload state when another tab saves ──
  useEffect(()=>{
    const onStorage=(e)=>{
      if(e.key!==SK||!e.newValue)return;
      try{
        const d=JSON.parse(e.newValue);
        if(d?.profiles?.length){
          setProfiles(d.profiles);
          if(d.parentPin!==undefined)setParentPin(d.parentPin);
          if(d.settings)setSettings(d.settings);
          if(d.daily)setDailyMap(d.daily);
        }
      }catch{}
    };
    window.addEventListener('storage',onStorage);
    return()=>window.removeEventListener('storage',onStorage);
  },[]);

  // ── CHECKOUT SUCCESS + PASSWORD RECOVERY DETECTION ──────
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const checkout=params.get('checkout');
    if(checkout==='success'){setCheckoutMsg('Welcome to Finley Premium! Your 30-day free trial has started.');setTimeout(()=>setCheckoutMsg(null),6000);}
    if(checkout){window.history.replaceState({},'',window.location.pathname);}
    // Handle Supabase password recovery redirect (check both query params and hash fragment)
    // Initialize Supabase client FIRST so it can process the recovery token from the hash
    const hashParams=new URLSearchParams(window.location.hash.replace('#','?'));
    if(params.get('type')==='recovery'||hashParams.get('type')==='recovery'){
      getSupabase(); // ensure client reads the hash token before we clear it
      setTimeout(()=>{
        setRecoveryMode(true);
        window.history.replaceState({},'',window.location.pathname);
      },500); // delay to let Supabase process the token
    }
  },[]);

  // ── LOAD FROM STORAGE ON MOUNT ──────────────────────────
  useEffect(()=>{
    loadApp().then(async(d)=>{
      // Check for existing Supabase session
      try{
        const session=await supaGetSession();
        if(session){
          const user=await supaGetUser();
          if(user)setAuthUser(user);
        }
      }catch{}
      if(d?.profiles?.length){
        // Migrate + archive before setting state
        d.profiles.forEach(p=>{
          checkAndArchiveSpelling(p.id);
          // Migration: add heroName + ageBracket for existing profiles
          if(!p.heroName)p.heroName='Explorer';
          if(!p.ageBracket)p.ageBracket=(p.age||7)<=6?'early':(p.age||7)<=9?'rising':'champion';
        });
        setProfiles(d.profiles);
        setParentPin(d.parentPin||null);
        const loadedSettings=normalizeSettings(d.settings||DEFAULT_SETTINGS);
        setSettings(loadedSettings);
        const today=todayStr();
        const dm=d.daily?.date===today?d.daily:{date:today,rounds:{},minutes:{}};
        if(!dm.minutes)dm.minutes={};
        setDailyMap(dm);
        const firstId=d.profiles[0].id;
        setApId(firstId);
        setSStars(0);
        
        if(d.profiles.length>1)setScreen("profiles");
        else openPrimaryFlowForProfile(d.profiles[0],{settingsOverride:{tmr:loadedSettings.tmr||15,finleyChoose:loadedSettings.finleyChoose!==false}});
        // Check for automated email triggers on app open
        checkAndSendEmailTriggers(d.profiles).catch(()=>{});
      }else{
        // No local profiles — if authenticated, try pulling from Supabase
        try{
          const session=await supaGetSession();
          if(session){
            const pulled=await pullFromSupabase();
            if(pulled?.profiles?.length){
              setProfiles(pulled.profiles);
              setParentPin(pulled.parentPin||null);
              const pulledSettings=normalizeSettings(pulled.settings||DEFAULT_SETTINGS);
              setSettings(pulledSettings);
              setApId(pulled.profiles[0].id);
              
              if(pulled.profiles.length>1)setScreen("profiles");
              else openPrimaryFlowForProfile(pulled.profiles[0],{settingsOverride:pulledSettings});
              setLoading(false);
              return;
            }
          }
        }catch{}
        // No profiles — route to onboarding (ParentSetup reads rq:oauthPending to restore step)
        setScreen('onboarding');
      }
      setLoading(false);
    }).catch(()=>setLoading(false));
  },[]);

  // ── AUTH STATE LISTENER (handles Google OAuth redirect) ──
  useEffect(()=>{
    const sb=getSupabase();
    if(!sb)return;
    const{data:{subscription}}=sb.auth.onAuthStateChange(async(event,session)=>{
      if(event==='SIGNED_IN'&&session?.user){
        setAuthUser(session.user);
        if(session.user.email)saveParentEmail(session.user.email);
        // If returning from OAuth with no profiles, route to step 3 (card) or step 4 (child profile)
        if(profiles.length===0&&!loading){
          try{
            const pulled=await pullFromSupabase();
            if(pulled?.profiles?.length){
              setProfiles(pulled.profiles);setParentPin(pulled.parentPin||null);
              const pulledSettings=normalizeSettings(pulled.settings||DEFAULT_SETTINGS);
              setSettings(pulledSettings);
              setApId(pulled.profiles[0].id);
              
              if(pulled.profiles.length>1)setScreen('profiles');
              else openPrimaryFlowForProfile(pulled.profiles[0],{settingsOverride:pulledSettings});
              return;
            }
          }catch{}
          // No profiles in Supabase either — they need to finish onboarding
          // ParentSetup will detect authUser and can skip auth step
        }
      }
      if(event==='SIGNED_OUT')setAuthUser(null);
    });
    return()=>subscription?.unsubscribe();
  },[profiles.length,loading]);

  // ── SAVE TO STORAGE ON CHANGES ──────────────────────────
  const initDone=useRef(false);
  const aiAnalysisDone=useRef(false);
  useEffect(()=>{
    if(loading)return;
    if(!initDone.current){initDone.current=true;return;}
    if(profiles.length>0){
      saveApp({profiles,parentPin,settings,daily:dailyMap});
      // Debounced Supabase sync (fire-and-forget, 5s delay)
      if(authUser){
        if(syncTimerRef.current)clearTimeout(syncTimerRef.current);
        syncTimerRef.current=setTimeout(()=>{
          syncToSupabase(profiles,settings,parentPin).catch(()=>{});
        },5000);
      }
    }
  },[profiles,parentPin,settings,dailyMap,loading,authUser]);

  const up=(fn)=>{setProfiles(ps=>ps.map(p=>p.id===apId?fn(p):p));};
  const handleRec=(mode,word,correct,ms)=>{
    const now = Date.now();
    const guessed = isGuess(ms, mode);
    const prospectiveLog = [...(tracker.log || []).slice(-4), { m: mode, w: word || null, c: correct, ms: ms || 0, lv: tracker.currentLevel, t: now }];
    const patternTap = isPatternTapping(prospectiveLog);
    if (!roundStatsRef.current || roundStatsRef.current.mode !== mode) {
      roundStatsRef.current = { mode, startedAt: now, attempted: 0, correct: 0, guessingTriggers: 0, patternTriggers: 0 };
    }
    roundStatsRef.current.attempted += 1;
    roundStatsRef.current.correct += correct ? 1 : 0;
    roundStatsRef.current.guessingTriggers += guessed ? 1 : 0;
    roundStatsRef.current.patternTriggers += patternTap ? 1 : 0;
    if (ap?.name) setPilotSyncContext({ childName: ap.name });
    up(p=>({...p,tracker:record(p.tracker,mode,word,correct,ms,p.age,p.name)}));
  };
  const handleDone=(stars)=>{
    const oldLevel=tracker.currentLevel;
    const preview=doneRound(tracker);
    up(p=>({...p,tracker:doneRound(p.tracker),totalStars:p.totalStars+stars}));
    if(preview.currentLevel>oldLevel)setLevelUpInfo(preview.currentLevel);
    setSStars(s=>s+stars);
    // Fire welcome email after first completed round
    if(tracker.rounds===0&&ap)checkAndSendWelcomeEmail(ap).catch(()=>{});
    const today=todayStr();
    setDailyMap(dm=>{
      const base=dm.date!==today?{date:today,rounds:{},minutes:{}}:dm;
      return{...base,date:today,rounds:{...base.rounds,[apId]:(base.rounds[apId]||0)+1},minutes:base.minutes||{}};
    });
    const roundStats = roundStatsRef.current || { mode: screen, startedAt: Date.now(), attempted: 0, correct: 0, guessingTriggers: 0, patternTriggers: 0 };
    const modeMap = { phonics: 'phonics', sight: 'sight_words', stories: 'stories', bubbles: 'bubble_pop' };
    const durationSeconds = Math.max(1, Math.round((Date.now() - (roundStats.startedAt || Date.now())) / 1000));
    const qualityScore = preview.sessionQuality?.[preview.sessionQuality.length - 1]?.sqs ?? null;
    const isPlaylistRound = !!playlist;
    const isLastPlaylistActivity = isPlaylistRound && playlistIdx >= playlist.length - 1;
    const currentPlaylistId = playlist ? (playlistIdRef.current || `${apId}:${sessionStartRef.current || Date.now()}`) : null;
    if (playlist && !playlistIdRef.current) playlistIdRef.current = currentPlaylistId;
    setSessionSummary((summary)=>({
      activitiesCompleted: summary.activitiesCompleted + 1,
      wordsAttempted: summary.wordsAttempted + (roundStats.attempted || 0),
      wordsCorrect: summary.wordsCorrect + (roundStats.correct || 0),
      score: summary.score + stars
    }));
    if (ap?.name) {
      void (async () => {
        try {
          const sessionId = await syncSession({
            childName: ap.name,
            mode: modeMap[roundStats.mode] || 'mixed',
            durationSeconds,
            wordsAttempted: roundStats.attempted,
            wordsCorrect: roundStats.correct,
            score: stars,
            qualityScore,
            guessingTriggers: roundStats.guessingTriggers,
            patternTriggers: roundStats.patternTriggers,
            chillModeActivated: !!preview.chillMode,
            playlistId: currentPlaylistId,
            levelAtSession: oldLevel
          });
          if (sessionId && preview.bkt) {
            await syncBKTSnapshot({
              childName: ap.name,
              sessionId,
              skillStates: preview.bkt
            });
          }
        } catch (err) {
          console.warn('Pilot sync failed, data queued for retry:', err?.message || err);
        }
      })();
    }
    roundStatsRef.current = null;
    if(expired){
      addSessionMinutes();timer.clear();setPlaylist(null);setPlaylistIdx(0);setScreen("session_done");
      playlistIdRef.current = null;
      if(online&&ap&&!aiAnalysisDone.current){aiAnalysisDone.current=true;analyzeSession(ap,online).then(profile=>{if(profile)up(p=>({...p,tracker:{...p.tracker,aiProfile:profile}}));});}
    }
    else if(isPlaylistRound && !isLastPlaylistActivity){
      const nextIdx = playlistIdx + 1;
      setPlaylistIdx(nextIdx);
      setScreen("playlist_transition");
      if(online)prefetchContent(tracker.currentLevel,APP_THEME.id,ap);
    }
    else if(isPlaylistRound && isLastPlaylistActivity){
      // Playlist exhausted — end session
      addSessionMinutes();timer.clear();setPlaylist(null);setPlaylistIdx(0);setScreen("session_done");
      playlistIdRef.current = null;
      if(online&&ap&&!aiAnalysisDone.current){aiAnalysisDone.current=true;analyzeSession(ap,online).then(profile=>{if(profile)up(p=>({...p,tracker:{...p.tracker,aiProfile:profile}}));});}
    }
    else{setScreen("mode");if(online)prefetchContent(tracker.currentLevel,APP_THEME.id,ap);}
  };
  const handleExpire=useCallback(()=>{setExpired(true);try{localStorage.removeItem('rq:timer-start');}catch{}},[]);
  // When timer expires on non-game screens, end session immediately
  // On game screens, the expired flag makes handleDone route to session_done (soft finish)
  const timer=useTimer(expired?0:settings.tmr,handleExpire);
  const openManualModeForProfile=(profile)=>{
    setPlaylist(null);setPlaylistIdx(0);setPlaylistSwaps(1);
    resetSessionSummary();
    playlistIdRef.current=null;sessionStartRef.current=null;roundStatsRef.current=null;
    if(online)prefetchContent(profile.tracker.currentLevel,APP_THEME.id,profile);
    setScreen('mode');
  };
  const openPrimaryFlowForProfile=(profile,opts={})=>{
    if(!profile)return false;
    const{fallbackToMode=true,settingsOverride=settings}=opts;
    setStoryToRead(null);
    setPlaylistIdx(0);setPlaylistSwaps(1);
    resetSessionSummary();
    playlistIdRef.current=null;roundStatsRef.current=null;sessionStartRef.current=null;
    if(settingsOverride.finleyChoose===false){
      if(fallbackToMode)openManualModeForProfile(profile);
      return false;
    }
    const nextPlaylist=buildSessionPlaylist(profile.tracker,profile,settingsOverride.tmr);
    if(nextPlaylist?.length){
      resetInsightSession();
      setPlaylist(nextPlaylist);
      if(online)prefetchContent(profile.tracker.currentLevel,APP_THEME.id,profile);
      setScreen('playlist_plan');
      return true;
    }
    setPlaylist(null);
    if(fallbackToMode)openManualModeForProfile(profile);
    return false;
  };
  const launchCurrentPlaylistActivity=()=>{
    if(!playlist?.[playlistIdx]){
      if(ap)openPrimaryFlowForProfile(ap);
      return;
    }
    if(!sessionStartRef.current)sessionStartRef.current=Date.now();
    if(!timer.resume())timer.start();
    if(online)prefetchContent(tracker.currentLevel,APP_THEME.id,ap);
    setScreen(playlist[playlistIdx].mode||'mode');
  };
  // If timer expires while not in a game, go to session_done immediately
  useEffect(()=>{if(expired&&["mode","playlist_start","playlist_plan","playlist_choice","playlist_progress","playlist_transition"].includes(screen)){setPlaylist(null);setPlaylistIdx(0);
    addSessionMinutes();setScreen("session_done");
    if(online&&ap&&!aiAnalysisDone.current){aiAnalysisDone.current=true;analyzeSession(ap,online).then(profile=>{if(profile)up(p=>({...p,tracker:{...p.tracker,aiProfile:profile}}));});}
  }},[expired,screen,apId]);
  // Safety: if on a playlist screen but playlist was cleared, rebuild or fall back to free play
  useEffect(()=>{if(!playlist&&["playlist_start","playlist_plan","playlist_choice","playlist_progress","playlist_transition"].includes(screen)){if(ap)openPrimaryFlowForProfile(ap);else setScreen("mode");}},[playlist,screen,apId]);
  // Handle password recovery redirect
  useEffect(()=>{if(recoveryMode&&!loading)setScreen("password_recovery");},[recoveryMode,loading]);
  const clearPlaylistFlow=()=>{setPlaylist(null);setPlaylistIdx(0);setPlaylistSwaps(1);playlistIdRef.current=null;};
  const reqPin=(target,source=screen)=>{
    if(target==='parent_settings'){
      setParentDashboardTab('settings');
      setPinTarget('dashboard');
    }else if(target==='dashboard'){
      setParentDashboardTab('home');
      setPinTarget('dashboard');
    }else{
      setPinTarget(target);
    }
    setPinSource(source);
    setScreen("pin_gate");
  };
  const showGearButton=!!ap&&!['parent_setup','onboarding','add_child','placement_briefing','placement_analysis','placement_results','reading_dna','pin_gate','sign_in','password_recovery','story_library','story_reading','parent_settings'].includes(screen);

  const selectChild=(id)=>{
    const nextProfile=profiles.find(p=>p.id===id);
    setApId(id);setSStars(0);setExpired(false);timer.clear();aiAnalysisDone.current=false;sessionStartRef.current=null;
    setPlaylist(null);setPlaylistIdx(0);setPlaylistSwaps(1);
    playlistIdRef.current = null;
    roundStatsRef.current = null;
    if(nextProfile)openPrimaryFlowForProfile(nextProfile);
  };
  const handleExtraPractice=()=>{
    if(!ap)return;
    setExpired(false);
    setSStars(0);
    timer.clear();
    aiAnalysisDone.current=false;
    sessionStartRef.current=null;
    clearPlaylistFlow();
    resetSessionSummary();
    openManualModeForProfile(ap);
  };

  const getPinCancelScreen=()=>{
    if(pinSource==='session_done')return 'session_done';
    if(pinSource==='profiles')return 'profiles';
    if(pinSource==='mode')return 'mode';
    if(pinSource==='dashboard')return 'dashboard';
    if(pinSource==='parent_settings')return 'parent_settings';
    if(pinTarget==='add_child')return profiles.length>1?'profiles':'mode';
    if(pinTarget==='dashboard'||pinTarget==='parent_settings')return 'mode';
    return pinSource||'mode';
  };
  const getTopBackTarget=()=>{
    if(screen==='mode')return profiles.length>1?'profiles':null;
    if(screen==='dashboard')return profiles.length>1?'profiles':settings.finleyChoose===false?'mode':null;
    if(screen==='playlist_start'||screen==='playlist_plan'||screen==='playlist_choice'||screen==='playlist_progress')return profiles.length>1?'profiles':settings.finleyChoose===false?'mode':null;
    if(screen==='phonics'||screen==='sight'||screen==='stories'||screen==='bubbles'){
      if(playlist)return 'playlist_progress';
      return profiles.length>1?'profiles':settings.finleyChoose===false?'mode':null;
    }
    if(screen==='session_done')return profiles.length>1?'profiles':settings.finleyChoose===false?'mode':null;
    return null;
  };
  const handleTopBack=()=>{
    const target=getTopBackTarget();
    const inPlaylistFlow=screen==='playlist_start'||screen==='playlist_plan'||screen==='playlist_choice'||screen==='playlist_progress'||screen==='playlist_transition'||screen==='phonics'||screen==='sight'||screen==='stories'||screen==='bubbles';
    const activeGameSession=['phonics','sight','stories','bubbles'].includes(screen)&&!!playlist;
    const activeSession=['playlist_choice','playlist_progress','playlist_transition'].includes(screen)&&!!playlist;
    if(activeGameSession){
      setScreen('playlist_progress');
      return;
    }
    if(activeSession){
      addSessionMinutes();timer.clear();clearPlaylistFlow();setScreen('session_done');return;
    }
    if(!target&&inPlaylistFlow){clearPlaylistFlow();return;}
    if(!target)return;
    if(inPlaylistFlow&&target!=='playlist_progress')clearPlaylistFlow();
    setScreen(target);
  };

  const handleSettingsSave=({tmr:newTmr,newPin,ageUpdates,deleteId,finleyChoose:fc,coachingPromptsEnabled,spellingWordMastery,spellingChildId,levelOverride,dailyLimitMin,difficultyOverride,todayExtensionMin,notificationPrefs,profileUpdate})=>{
    setSettings((s)=>{
      const nextNotificationPrefs = notificationPrefs
        ? (() => {
            const { sessionReminders, ...rest } = notificationPrefs;
            return { ...(s.notificationPrefs || {}), ...rest };
          })()
        : (s.notificationPrefs || DEFAULT_SETTINGS.notificationPrefs);
      const next = {
        ...s,
        tmr: newTmr ?? s.tmr,
        finleyChoose: fc!==undefined?fc:s.finleyChoose,
        coachingPromptsEnabled: coachingPromptsEnabled ?? s.coachingPromptsEnabled,
        dailyLimitMin: dailyLimitMin ?? s.dailyLimitMin,
        difficultyOverride: difficultyOverride ?? s.difficultyOverride,
        todayExtensionMin: todayExtensionMin ?? s.todayExtensionMin,
        todayExtensionDate: todayExtensionMin != null ? todayStr() : s.todayExtensionDate,
        notificationPrefs: nextNotificationPrefs
      };
      return normalizeSettings(next);
    });
    if(newPin)setParentPin(newPin);
    if(ageUpdates)setProfiles(ps=>ps.map(p=>ageUpdates[p.id]!=null?{...p,age:ageUpdates[p.id]}:p));
    if(profileUpdate){
      setProfiles((ps)=>ps.map((p)=>{
        if(p.id!==profileUpdate.childId)return p;
        return {
          ...p,
          name: profileUpdate.name ?? p.name,
          age: profileUpdate.age ?? p.age,
          avatar: profileUpdate.avatar ?? p.avatar
        };
      }));
    }
    if(spellingWordMastery&&spellingChildId)setProfiles(ps=>ps.map(p=>p.id===spellingChildId?{...p,tracker:{...p.tracker,wordMastery:{...(p.tracker.wordMastery||{}),...spellingWordMastery}}}:p));
    if(levelOverride){
      const{childId,newLevel}=levelOverride;
      setProfiles(ps=>ps.map(p=>{
        if(p.id!==childId)return p;
        const newBkt=initBkt(newLevel);
        const newHistory=[...(p.tracker.levelHistory||[]),{level:newLevel,from:p.tracker.currentLevel,date:new Date().toISOString(),source:'parent_override'}];
        return{...p,tracker:{...p.tracker,currentLevel:newLevel,bkt:newBkt,roundsAtLevel:0,levelHistory:newHistory,chillMode:false,chillRoundsRemaining:0}};
      }));
    }
    if(deleteId&&profiles.length>1){
      setProfiles(ps=>{const np=ps.filter(p=>p.id!==deleteId);if(apId===deleteId&&np.length>0){setApId(np[0].id);}return np;});
      setDailyMap(dm=>{const nr={...dm.rounds};delete nr[deleteId];const nm={...(dm.minutes||{})};delete nm[deleteId];return{...dm,rounds:nr,minutes:nm};});
      // Route: if >1 remaining go to profiles, otherwise return to the primary child flow
      const remaining=profiles.filter(p=>p.id!==deleteId);
      if(remaining.length>1)setScreen("profiles");
      else if(remaining[0])openPrimaryFlowForProfile(remaining[0]);
    }
  };

  const completePlacement=(lv,meta=null,{skipAnalysis=false,nextAction='start'}={})=>{
    const nextTracker=createTracker(lv);
    setProfiles(ps=>ps.map(p=>p.id===apId?{...p,tracker:nextTracker}:p));
    if(meta&&ap?.name){
      syncAssessment({
        childName:ap.name,
        assessmentType:'placement',
        levelResult:lv,
        rawSkillScores:meta?.rawSkillScores||null,
        durationSeconds:meta?.durationSeconds||null
      }).catch(()=>{});
    }
    setPlacementLevel(lv);
    setPlacementNextAction(nextAction);
    setScreen(skipAnalysis?'placement_results':'placement_analysis');
  };

  const skipPlacementToLevelOne=()=>completePlacement(1,null,{skipAnalysis:true,nextAction:placementNextAction});
  const startRetakePlacement=()=>{
    setPlacementNextAction('dashboard');
    setPlacementLevel(null);
    setScreen('placement_briefing');
  };
  const handlePlacementAnalysisDone=()=>setScreen('placement_results');
  const handlePlacementContinue=()=>{
    if(placementNextAction==='dashboard'){setScreen('dashboard');return;}
    if(placementNextAction==='start'&&profiles.length===1){setScreen('reading_dna');return;}
    const nextProfile=profiles.find(p=>p.id===apId);
    if(nextProfile){openPrimaryFlowForProfile(nextProfile);return;}
    setScreen('mode');
  };

  // ── LOADING SCREEN ──────────────────────────────────────
  if(loading)return <div style={{height:"100%",background:"#F4F5F7",display:"flex",justifyContent:"center",alignItems:"center"}}><div style={{textAlign:"center"}}><div style={{fontSize:72,marginBottom:16,animation:"float 2s ease-in-out infinite"}}>📖</div><div style={{fontFamily:F,fontSize:28,color:C.text}}>Finley</div><div style={{width:44,height:44,border:"4px solid #F0F0F5",borderTopColor:C.primary,borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"20px auto 0"}}/></div></div>;

  const isOceanScreen=OCEAN_SCREENS.includes(screen);
  const isGameScreen=["phonics","sight","stories","bubbles"].includes(screen);
  const isTabletLayout=viewportWidth>=768&&viewportWidth<1024;
  const isDesktopLayout=viewportWidth>=1024;
  const shellBackground=isDesktopLayout
    ? 'radial-gradient(circle at top left, rgba(0,180,216,0.2), transparent 26%), radial-gradient(circle at bottom left, rgba(255,107,53,0.16), transparent 28%), radial-gradient(circle at top right, rgba(255,209,102,0.12), transparent 22%), linear-gradient(135deg, #061928 0%, #0A2540 48%, #0D3050 100%)'
    : isGameScreen&&ct?.bg?ct.bg:isOceanScreen?'#060A14':'#F4F5F7';
  const shellPadding=isDesktopLayout?24:isTabletLayout?20:12;
  const appMaxWidth=isDesktopLayout?520:isTabletLayout?600:460;
  const settingsTabProps={
    settings,
    authUser,
    onSave:handleSettingsSave,
    onAddChild:()=>setScreen('add_child'),
    onRetakePlacement:startRetakePlacement,
    onSignIn:()=>setScreen('sign_in'),
    onSignOut:async()=>{try{await supaSignOut();}catch{}setAuthUser(null);}
  };
  return <div style={{height:"100dvh",background:shellBackground,transition:'background 0.5s ease',display:"flex",justifyContent:"center",alignItems:"stretch",padding:shellPadding,overflow:"hidden",position:'relative'}}>
    {isOceanScreen&&<OceanBg/>}
    {isDesktopLayout&&<>
      <div style={{position:'absolute',top:'10%',left:'12%',width:220,height:220,borderRadius:'50%',background:'radial-gradient(circle, rgba(0,180,216,0.14) 0%, rgba(0,180,216,0) 72%)',pointerEvents:'none'}}/>
      <div style={{position:'absolute',bottom:'8%',right:'10%',width:260,height:260,borderRadius:'50%',background:'radial-gradient(circle, rgba(255,107,53,0.12) 0%, rgba(255,107,53,0) 72%)',pointerEvents:'none'}}/>
    </>}
    <div style={{width:"100%",maxWidth:isDesktopLayout?1180:appMaxWidth,height:"100%",minHeight:0,display:"flex",justifyContent:"center",alignItems:"stretch",gap:isDesktopLayout?32:0,margin:"0 auto",position:'relative',zIndex:1}}>
    {isDesktopLayout&&<DesktopContextPanel/>}
    <div style={{width:"100%",maxWidth:appMaxWidth,height:"100%",maxHeight:"100%",background:C.bg,borderRadius:isDesktopLayout||isTabletLayout?28:28,border:isOceanScreen?'1px solid rgba(255,255,255,0.1)':'1px solid #E5E7EB',boxShadow:isGameScreen&&ct?.bg?'0 8px 40px rgba(0,0,0,0.25)':isOceanScreen?'0 8px 40px rgba(0,0,0,0.4)':isDesktopLayout?'0 24px 80px rgba(0,0,0,0.22)':'0 8px 40px rgba(0,0,0,.08)',overflow:"hidden",display:"flex",flexDirection:"column",minHeight:0,position:"relative",zIndex:1,backdropFilter:isDesktopLayout?'blur(8px)':undefined}}>
      {!online&&<div style={{background:"#FEF3C7",borderBottom:"2px solid #F59E0B",padding:"8px 16px",textAlign:"center"}}><span style={{fontFamily:N,fontSize:13,color:"#92400E",fontWeight:700}}>{tracker.currentLevel>=4&&(ccCount('phonics',tracker.currentLevel,APP_THEME.id)>0||ccCount('stories',tracker.currentLevel,APP_THEME.id)>0)?"📡 Offline — using saved content":"📡 Offline — using practice content"}</span></div>}
      {saveFail&&<div style={{background:"#FEE2E2",borderBottom:"2px solid #EF4444",padding:"8px 16px",textAlign:"center"}}><span style={{fontFamily:N,fontSize:13,color:"#991B1B",fontWeight:700}}>⚠️ Storage full — progress may not save. Clear browser data or free up space.</span></div>}
      {!["parent_setup","onboarding","add_child","placement_briefing","placement_analysis","placement_results","reading_dna","quiz","pin_gate","profiles","parent_settings","playlist_transition","story_library","story_reading","sign_in","password_recovery"].includes(screen)&&<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 18px",borderBottom:"2px solid #F0F0F5",flexShrink:0}}>
        {getTopBackTarget()?<button onClick={handleTopBack} style={{background:"#F0F0F5",border:"none",borderRadius:10,padding:"6px 14px",color:C.textMid,fontFamily:N,fontSize:13,cursor:"pointer",fontWeight:600}}>← Back</button>:<div style={{width:60}}/>}
        <div style={{flex:1,margin:"0 12px"}}>{(()=>{const a=ap?.age||7;const tv=settings.tmr>0&&!expired&&timer.active;const progressLabel=playlist&&isGameScreen?`Activity ${playlistIdx+1} of ${playlist.length}`:null;if(!tv||a<=6)return progressLabel?<div style={{fontFamily:N,fontSize:12,color:C.textMid,textAlign:'center',fontWeight:700}}>{progressLabel}</div>:null;if(a<=9){const pct=timer.limitMin>0?100-((timer.min*60+timer.sec)/(timer.limitMin*60))*100:0;return <div>{progressLabel&&<div style={{fontFamily:N,fontSize:11,color:C.textMid,textAlign:'center',fontWeight:700,marginBottom:4}}>{progressLabel}</div>}<div style={{height:6,background:"#F0F0F5",borderRadius:3,overflow:"hidden"}}><div style={{height:"100%",width:`${pct}%`,background:ct.color,borderRadius:3,transition:"width 1s linear"}}/></div></div>;}return <div>{progressLabel&&<div style={{fontFamily:N,fontSize:11,color:C.textMid,textAlign:'center',fontWeight:700,marginBottom:4}}>{progressLabel}</div>}<TB min={timer.min} sec={timer.sec} totalMin={timer.limitMin||settings.tmr} color={ct.color}/></div>;})()}</div>
        <div style={{fontFamily:F,fontSize:15,color:C.primary,fontWeight:700,marginRight:showGearButton?44:0}}>⭐ {ap?.totalStars||0}</div>
      </div>}
      <AnimatePresence mode="wait">
      <motion.div key={screen} {...screenTransition} style={{flex:1,minHeight:0,overflow:"auto",WebkitOverflowScrolling:"touch"}}>
        {screen==="parent_setup"&&<ParentSetup authUser={authUser} onComplete={(cfg)=>{try{localStorage.setItem('rq:parentSetupDone','true');}catch{}const p=createProfile(cfg.cn,cfg.ca,cfg.ints,cfg.conf,cfg.focus,cfg.goal);p.heroName=cfg.heroName||'Explorer';p.ageBracket=cfg.ageBracket||'rising';try{if(localStorage.getItem('rq:hasVPC')==='true')p.hasVPC=true;p.stripeCustomerId=localStorage.getItem('rq:stripeCustomer')||null;}catch{}try{localStorage.setItem(`rq:interests:${p.id}`,JSON.stringify(cfg.ints||[]));}catch{}if(cfg.pin)setParentPin(cfg.pin);setProfiles([p]);setApId(p.id);setSettings(s=>normalizeSettings({...s,tmr:cfg.tmr||15}));setDailyMap({date:todayStr(),rounds:{},minutes:{}});setPlacementNextAction('start');setScreen("placement_briefing");}} onAuthChange={(u)=>{if(u)setAuthUser(u);}}/>}
        {screen==="sign_in"&&<SignInScreen onSuccess={(data,pulled)=>{const user=data?.user||data?.session?.user;if(user)setAuthUser(user);if(pulled?.profiles?.length&&profiles.length===0){
          // No local data — use Supabase data
          const pulledSettings=normalizeSettings(pulled.settings||DEFAULT_SETTINGS);setProfiles(pulled.profiles);setParentPin(pulled.parentPin||null);setSettings(pulledSettings);setApId(pulled.profiles[0].id);if(pulled.profiles.length>1)setScreen("profiles");else openPrimaryFlowForProfile(pulled.profiles[0],{settingsOverride:pulledSettings});
        }else if(profiles.length>0){
          // Local data exists — keep it (localStorage wins), push to Supabase
          if(profiles.length>1)setScreen("profiles");else if(profiles[0])openPrimaryFlowForProfile(profiles[0]);syncToSupabase(profiles,settings,parentPin).catch(()=>{});
        }else{setScreen("onboarding");}}} onBack={()=>{setScreen(profiles.length>1?"profiles":profiles.length===1?"mode":"onboarding");}}/>}
        {screen==="password_recovery"&&<PasswordRecoveryScreen onDone={()=>{setRecoveryMode(false);if(profiles.length>1)setScreen("profiles");else if(profiles.length===1)openPrimaryFlowForProfile(profiles[0]);else setScreen("onboarding");}}/>}
        {screen==="onboarding"&&<ParentSetup authUser={authUser} onComplete={(cfg)=>{try{localStorage.setItem('rq:parentSetupDone','true');}catch{}const p=createProfile(cfg.cn,cfg.ca,cfg.ints,cfg.conf,cfg.focus,cfg.goal);p.heroName=cfg.heroName||'Explorer';p.ageBracket=cfg.ageBracket||'rising';try{if(localStorage.getItem('rq:hasVPC')==='true')p.hasVPC=true;p.stripeCustomerId=localStorage.getItem('rq:stripeCustomer')||null;}catch{}try{localStorage.setItem(`rq:interests:${p.id}`,JSON.stringify(cfg.ints||[]));}catch{}if(cfg.pin)setParentPin(cfg.pin);setProfiles([p]);setApId(p.id);setSettings(s=>normalizeSettings({...s,tmr:cfg.tmr||15}));setDailyMap({date:todayStr(),rounds:{},minutes:{}});setPlacementNextAction('start');setScreen("placement_briefing");}} onAuthChange={(u)=>{if(u)setAuthUser(u);}}/>}
        {screen==="add_child"&&<Onboarding isAdd={true} onComplete={(cfg)=>{const p=createProfile(cfg.cn,cfg.ca,cfg.ints,cfg.conf,cfg.focus,cfg.goal);p.heroName=cfg.heroName||'Explorer';p.ageBracket=cfg.ageBracket||'rising';try{localStorage.setItem(`rq:interests:${p.id}`,JSON.stringify(cfg.ints||[]));localStorage.setItem(`rq:goal:${p.id}`,cfg.goal||"independent");}catch{}setProfiles(ps=>[...ps,p]);setApId(p.id);aiAnalysisDone.current=false;setPlacementNextAction('start');setScreen("placement_briefing");}}/>}
        {screen==="placement_briefing"&&<PlacementBriefing age={ap?.age} onStart={()=>setScreen("quiz")} onSkip={skipPlacementToLevelOne}/>}
        {screen==="placement_analysis"&&placementLevel!=null&&<PlacementAnalysis onDone={handlePlacementAnalysisDone} durationMs={5000}/>}
        {screen==="placement_results"&&placementLevel!=null&&<PlacementResults level={placementLevel} ctaMode={placementNextAction==='dashboard'?'dashboard':'start'} onContinue={handlePlacementContinue}/>}
        {screen==="reading_dna"&&ap&&<ReadingDNA profile={ap} tracker={tracker} onboardingData={onboardingDataForDNA} onContinue={()=>{try{localStorage.removeItem('rq:onboardingStep');localStorage.removeItem('rq:onboardingData');}catch{}if(settings.finleyChoose!==false)openPrimaryFlowForProfile(ap);else setScreen('mode');}}/>}
        {screen==="profiles"&&<Profiles profiles={profiles} onSelect={selectChild} onAdd={()=>reqPin("add_child")} authUser={authUser} onSignIn={()=>setScreen("sign_in")}/>}
        {screen==="quiz"&&<Quiz name={ap?.name||""} conf={ap?.conf} age={ap?.age} onComplete={(lv,meta,opts={})=>completePlacement(lv,meta,{...opts,nextAction:placementNextAction})} onCancel={profiles.length>1?()=>setScreen("profiles"):null}/>}
        {screen==="mode"&&<ModePicker theme={ct} tracker={tracker} onPick={(nextMode)=>{if(!sessionStartRef.current)sessionStartRef.current=Date.now();if(!timer.resume())timer.start();setScreen(nextMode);}} onDash={()=>reqPin("dashboard")} onSettings={()=>reqPin("parent_settings")} onStories={()=>setScreen("story_library")}/>}
        {screen==="dashboard"&&<ParentDashboard profiles={profiles} activeId={apId} initialTab={parentDashboardTab} onSelectChild={(id)=>setApId(id)} onAddChild={()=>setScreen("add_child")} onStartSession={()=>{setParentDashboardTab('home');if(ap&&settings.finleyChoose!==false)openPrimaryFlowForProfile(ap);else setScreen("mode");}} onExtraPractice={handleExtraPractice} onOpenStories={()=>setScreen("story_library")} onClose={()=>{setParentDashboardTab('home');if(ap&&settings.finleyChoose!==false)openPrimaryFlowForProfile(ap);else setScreen("mode");}} settingsTabProps={settingsTabProps} renderProgress={(profile)=><Dash tracker={profile?.tracker||tracker} name={profile?.name||ap?.name||""} age={profile?.age||ap?.age||6} minutesToday={(dailyMap.minutes?.[profile?.id||apId]||0)+getSessionMinutes()} onClose={()=>{setParentDashboardTab('home');if(ap&&settings.finleyChoose!==false)openPrimaryFlowForProfile(ap);else setScreen("mode");}} childId={profile?.id||apId}/>} />}
        {screen==="parent_settings"&&<ParentDashboard profiles={profiles} activeId={apId} initialTab="settings" onSelectChild={(id)=>setApId(id)} onAddChild={()=>setScreen("add_child")} onStartSession={()=>{setParentDashboardTab('home');if(ap&&settings.finleyChoose!==false)openPrimaryFlowForProfile(ap);else setScreen("mode");}} onExtraPractice={handleExtraPractice} onOpenStories={()=>setScreen("story_library")} onClose={()=>{setParentDashboardTab('home');if(ap&&settings.finleyChoose!==false)openPrimaryFlowForProfile(ap);else setScreen("mode");}} settingsTabProps={settingsTabProps} renderProgress={(profile)=><Dash tracker={profile?.tracker||tracker} name={profile?.name||ap?.name||""} age={profile?.age||ap?.age||6} minutesToday={(dailyMap.minutes?.[profile?.id||apId]||0)+getSessionMinutes()} onClose={()=>{setParentDashboardTab('home');if(ap&&settings.finleyChoose!==false)openPrimaryFlowForProfile(ap);else setScreen("mode");}} childId={profile?.id||apId}/>} />}
        {screen==="playlist_start"&&<PlaylistStart name={ap?.name||""} playlist={playlist||[]} onStart={launchCurrentPlaylistActivity}/>}
        {screen==="playlist_plan"&&playlist&&<PlaylistPlan playlist={playlist} name={ap?.name||""} currentIdx={playlistIdx} onStart={launchCurrentPlaylistActivity} onReorder={(from,to)=>{const np=[...playlist];[np[from],np[to]]=[np[to],np[from]];setPlaylist(np);}} onSwap={(idx,newMode)=>{const np=[...playlist];np[idx]={...np[idx],mode:newMode,reason:'Your choice'};setPlaylist(np);setPlaylistSwaps(s=>s-1);}} swapsLeft={playlistSwaps} bubbleUnlocked={tracker.bubbleUnlocked} coachingPromptsEnabled={settings.coachingPromptsEnabled!==false}/>}
        {screen==="playlist_transition"&&<PlaylistTransition age={ap?.age||7} currentIdx={playlistIdx} total={playlist?.length||0} onDone={launchCurrentPlaylistActivity}/>}
        {screen==="playlist_choice"&&playlist&&(()=>{const remaining=playlist.slice(playlistIdx);const opts=remaining.slice(0,2);if(opts.length<=1)return <PlaylistTransition age={ap?.age||7} currentIdx={playlistIdx} total={playlist?.length||0} onDone={launchCurrentPlaylistActivity}/>;return <PlaylistChoice options={opts} name={ap?.name||""} currentIdx={playlistIdx} total={playlist?.length||0} onPick={(item)=>{if(item.mode!==remaining[0].mode&&remaining.length>=2){const np=[...playlist];[np[playlistIdx],np[playlistIdx+1]]=[np[playlistIdx+1],np[playlistIdx]];setPlaylist(np);}if(!sessionStartRef.current)sessionStartRef.current=Date.now();if(!timer.resume())timer.start();setScreen(item.mode);}}/>;})()}
        {screen==="playlist_progress"&&playlist&&<PlaylistProgress playlist={playlist} currentIdx={playlistIdx} onContinue={launchCurrentPlaylistActivity}/>}
        {screen==="pin_gate"&&<PinGate pin={parentPin} onUnlock={()=>{setScreen(pinTarget||"mode");setPinTarget(null);setPinSource(null);}} onSetPin={(pin)=>setParentPin(pin)} onCancel={()=>{setPinTarget(null);setScreen(getPinCancelScreen());setPinSource(null);}}/>}
        {screen==="session_done"&&<SessionDone name={ap?.name||""} stars={sStars} minutesPracticed={minutesToday+getSessionMinutes()} aiProfile={ap?.tracker?.aiProfile} tracker={tracker} age={ap?.age||6} parentPin={parentPin} expired={expired} childId={apId} sessionSummary={sessionSummary} coachingPromptsEnabled={settings.coachingPromptsEnabled!==false} onOpenStories={()=>setScreen("story_library")} onExtraPractice={handleExtraPractice} onSetPin={(pin)=>setParentPin(pin)} onViewDashboard={()=>reqPin("dashboard","session_done")} onUnlock={()=>{setExpired(false);setSStars(0);timer.clear();aiAnalysisDone.current=false;sessionStartRef.current=null;clearPlaylistFlow();resetSessionSummary();setScreen(profiles.length>1?"profiles":settings.finleyChoose===false?"mode":"playlist_plan");if(profiles.length===1&&settings.finleyChoose!==false&&ap)openPrimaryFlowForProfile(ap);}}/>}
        {screen==="phonics"&&<PhonicsGame theme={GAME_MODE_THEMES.phonics} tracker={tracker} profile={ap} onComplete={handleDone} onRecord={handleRec} online={online} sessionActivityIndex={playlist?playlistIdx:0} isPlaylistSession={!!playlist}/>}
        {screen==="sight"&&<SightGame tracker={tracker} profile={ap} onComplete={handleDone} onRecord={handleRec} online={online} sessionActivityIndex={playlist?playlistIdx:0} isPlaylistSession={!!playlist}/>}
        {screen==="stories"&&<StoryGame theme={GAME_MODE_THEMES.stories} tracker={tracker} profile={ap} onComplete={handleDone} onRecord={handleRec} online={online} sessionActivityIndex={playlist?playlistIdx:0} isPlaylistSession={!!playlist}/>}
        {screen==="bubbles"&&<BubblePop theme={GAME_MODE_THEMES.bubbles} tracker={tracker} profile={ap} onComplete={handleDone} onRecord={handleRec} sessionActivityIndex={playlist?playlistIdx:0} isPlaylistSession={!!playlist}/>}
        {screen==="story_library"&&ap&&<StoryLibrary profile={ap} tracker={tracker} onRead={(s)=>{setStoryToRead(s);setScreen("story_reading");}} onBack={()=>{if(expired)setScreen("session_done");else if(settings.finleyChoose!==false)openPrimaryFlowForProfile(ap);else setScreen("mode");}} onUpgrade={()=>setPricingOpen(true)}/>}
        {screen==="story_reading"&&storyToRead&&<StoryReading story={storyToRead} profile={ap} coachingPromptsEnabled={settings.coachingPromptsEnabled!==false} onBack={()=>setScreen("story_library")}/>}
      </motion.div>
      </AnimatePresence>
      {showGearButton&&<button onClick={()=>reqPin('parent_settings')} aria-label="Parent settings" style={{position:'absolute',top:18,right:18,zIndex:20,width:44,height:44,borderRadius:'50%',border:'none',background:'rgba(0,0,0,0.18)',backdropFilter:'blur(8px)',WebkitBackdropFilter:'blur(8px)',color:'rgba(255,255,255,0.85)',fontSize:18,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 2px 8px rgba(0,0,0,0.22)'}}>⚙</button>}
      {fbVisible&&<FeedbackButton onClick={()=>{setFbScreen(screen);setFbOpen(true);}}/>}
      {fbOpen&&<FeedbackModal onClose={()=>setFbOpen(false)} screen={fbScreen} level={tracker.currentLevel} childAge={ap?.age}/>}
      {levelUpInfo&&<LevelUpCeremony level={levelUpInfo} onDismiss={()=>setLevelUpInfo(null)}/>}
      {pricingOpen&&<PricingModal onClose={()=>setPricingOpen(false)} childId={apId}/>}
      {checkoutMsg&&<div style={{position:'fixed',top:20,left:'50%',transform:'translateX(-50%)',zIndex:10001,background:'linear-gradient(135deg, #4ADE80, #06D6A0)',color:'#fff',fontFamily:F,fontSize:15,fontWeight:600,padding:'14px 24px',borderRadius:16,boxShadow:'0 8px 30px rgba(74,222,128,0.4)',animation:'rqFadeIn 0.4s both',maxWidth:360,textAlign:'center'}}>{"🐬"} {checkoutMsg}</div>}
    </div>
    </div>
  </div>;
}
